import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginInputSchema, type LoginInput } from '@imza/shared';
import { useLogin } from '../lib/auth.js';
import { useToast } from '../components/ui/Toast.js';
import { Button } from '../components/ui/Button.js';
import { Logo } from '../components/Logo.js';
import { useState } from 'react';
import { Eye, EyeOff, Phone, MessageCircle, TrendingUp, Search } from 'lucide-react';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const login = useLogin();
  const { toast } = useToast();
  const [showPwd, setShowPwd] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({ resolver: zodResolver(LoginInputSchema) });

  async function onSubmit(data: LoginInput) {
    login.mutate(data, {
      onSuccess: () => navigate({ to: '/' }),
      onError: (err) =>
        toast(err instanceof Error ? err.message : 'Login failed', 'error'),
    });
  }

  return (
    <div className="min-h-screen bg-[#f4f5f8] p-0 md:p-6 lg:p-10">
      <div className="bg-white rounded-none md:rounded-3xl shadow-none md:shadow-[0_2px_24px_-4px_rgba(0,0,0,0.06)] min-h-screen md:min-h-[min(900px,calc(100vh-5rem))] grid md:grid-cols-2 overflow-hidden">

        {/* LEFT — login form */}
        <div className="flex flex-col justify-between px-6 py-10 md:px-12 lg:px-20 md:py-14">
          {/* Logo (icon only) */}
          <Logo size={56} />

          {/* Form */}
          <div className="max-w-sm w-full mx-auto md:mx-0 mt-12 md:mt-0">
            <h1 className="text-3xl md:text-4xl font-bold text-primary-text tracking-tight">
              Login to Dashboard
            </h1>
            <p className="text-secondary mt-2">Fill the below form to login</p>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5" noValidate>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-primary-text mb-2">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  autoComplete="email"
                  autoCapitalize="none"
                  className={`w-full h-12 px-4 rounded-xl border bg-white text-primary-text placeholder:text-secondary/60 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15 ${errors.email ? 'border-danger' : 'border-border'}`}
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-danger text-xs mt-1.5">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-primary-text mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPwd ? 'text' : 'password'}
                    placeholder="Enter Password"
                    autoComplete="current-password"
                    className={`w-full h-12 px-4 pr-12 rounded-xl border bg-white text-primary-text placeholder:text-secondary/60 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15 ${errors.password ? 'border-danger' : 'border-border'}`}
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary-text p-2 rounded-md"
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                  >
                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-danger text-xs mt-1.5">{errors.password.message}</p>
                )}
              </div>

              <button
                type="button"
                onClick={() => toast('Contact your admin to reset your password.', 'info')}
                className="text-primary text-sm font-medium hover:underline"
              >
                Forgot Password?
              </button>

              <Button
                type="submit"
                loading={login.isPending}
                size="lg"
                className="w-full h-12 rounded-xl text-base"
              >
                Login
              </Button>
            </form>
          </div>

          {/* Footer spacer */}
          <div className="text-xs text-secondary mt-12 md:mt-0">
            © {new Date().getFullYear()} Imza • Landlord CRM
          </div>
        </div>

        {/* RIGHT — marketing / preview panel (hidden on small screens) */}
        <div className="hidden md:flex bg-gradient-to-br from-[#eef1ff] via-[#f4f6ff] to-[#fafbff] flex-col p-10 lg:p-14 relative overflow-hidden">
          {/* subtle grid backdrop */}
          <div
            className="absolute inset-0 opacity-[0.4] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(#dee3f3 1px, transparent 1px), linear-gradient(90deg, #dee3f3 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />

          <div className="relative">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/80 backdrop-blur text-xs font-medium text-primary-text shadow-sm border border-border">
              IMZA Landlord Intelligence
            </span>
            <h2 className="mt-5 text-2xl lg:text-3xl font-bold text-primary-text leading-snug max-w-md">
              Manage your landlord pipeline{' '}
              <span className="text-primary">more professionally</span>
            </h2>
          </div>

          {/* Dashboard mock */}
          <div className="relative mt-8 flex-1">
            <div className="bg-white rounded-2xl shadow-[0_8px_32px_-8px_rgba(15,23,42,0.12)] border border-border overflow-hidden">
              {/* mock header bar */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-white">
                <Logo size={28} />
                <div className="text-xs text-secondary">Welcome to Overview</div>
              </div>

              <div className="grid grid-cols-[140px_1fr]">
                {/* mock sidebar */}
                <div className="border-r border-border p-3 space-y-1 bg-[#fafbff]">
                  <div className="px-3 py-2 rounded-md bg-primary/10 text-primary text-xs font-semibold">Overview</div>
                  <div className="px-3 py-2 text-xs text-secondary">Landlords</div>
                  <div className="px-3 py-2 text-xs text-secondary">Activity</div>
                  <div className="px-3 py-2 text-xs text-secondary">Kanban</div>
                  <div className="px-3 py-2 text-xs text-secondary">Admin</div>
                </div>

                {/* mock content */}
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <KpiTile label="Landlords" value="272K" icon={<Search size={12} />} />
                    <KpiTile label="Calls today" value="184" icon={<Phone size={12} />} />
                    <KpiTile label="Contact rate" value="62%" icon={<TrendingUp size={12} />} />
                  </div>

                  <div className="rounded-lg border border-border p-3">
                    <div className="text-[11px] text-secondary mb-2">Recent activity</div>
                    <div className="space-y-2">
                      <ActivityRow
                        time="14:02"
                        icon={<Phone size={11} />}
                        text="Call answered — Ahmed Al Mansoori"
                        tone="success"
                      />
                      <ActivityRow
                        time="13:48"
                        icon={<MessageCircle size={11} />}
                        text="WhatsApp sent — Mohammed Khan"
                        tone="primary"
                      />
                      <ActivityRow
                        time="13:11"
                        icon={<Phone size={11} />}
                        text="No answer — Fatima Al Zahra"
                        tone="muted"
                      />
                      <ActivityRow
                        time="12:30"
                        icon={<TrendingUp size={11} />}
                        text="Stage → Negotiation — Bayut Lead #4172"
                        tone="primary"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* floating chip */}
            <div className="absolute -top-3 right-2 bg-white rounded-xl shadow-md border border-border px-3 py-2 text-xs flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-success" />
              <span className="font-medium text-primary-text">272,244 landlords live</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-white p-2.5">
      <div className="flex items-center gap-1.5 text-[10px] text-secondary">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-base font-bold text-primary-text mt-0.5">{value}</div>
    </div>
  );
}

function ActivityRow({
  time,
  icon,
  text,
  tone,
}: {
  time: string;
  icon: React.ReactNode;
  text: string;
  tone: 'success' | 'primary' | 'muted';
}) {
  const toneClass =
    tone === 'success'
      ? 'bg-success/10 text-success'
      : tone === 'primary'
        ? 'bg-primary/10 text-primary'
        : 'bg-hover-bg text-secondary';
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className="text-secondary tabular-nums w-9">{time}</span>
      <span className={`h-5 w-5 rounded-md flex items-center justify-center ${toneClass}`}>
        {icon}
      </span>
      <span className="text-primary-text truncate">{text}</span>
    </div>
  );
}
